import React, { useRef, useEffect, useMemo, useState } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Html } from '@react-three/drei'
import * as THREE from 'three'
import { Obstacle } from '../../hooks/usePhysicsSurvivalRunner'

const PLAYER_Z_POSITION = 0.3

// Mobile detection và quality settings
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
                 (window.innerWidth <= 768)

// Geometry quality settings - giảm complexity trên mobile
const GEOMETRY_QUALITY = {
  sphereSegments: isMobile ? 16 : 32,
  cylinderSegments: isMobile ? 8 : 16,
  coneSegments: isMobile ? 6 : 8
}

interface SurvivalRunnerSceneProps {
  currentLane: number // 0: trái, 1: giữa, 2: phải
  obstacles: Obstacle[]
  currentQuestion: string
  speed: number
  isJumping: boolean
}

/**
 * Component nhân vật chạy - Lấy từ DoorQuest game
 */
const RunningCharacter: React.FC<{ lane: number; speed: number; isJumping: boolean }> = ({ lane, speed, isJumping }) => {
  const meshRef = useRef<THREE.Group>(null)
  const headRef = useRef<THREE.Group>(null)
  const leftArmRef = useRef<THREE.Group>(null)
  const rightArmRef = useRef<THREE.Group>(null)
  const leftLegRef = useRef<THREE.Group>(null)
  const rightLegRef = useRef<THREE.Group>(null)
  const jumpStartTimeRef = useRef<number | null>(null)
  const jumpStartXRef = useRef<number>(0)

  // Vị trí X của 3 làn: -2 (trái), 0 (giữa), 2 (phải)
  const lanePositions = [-2, 0, 2]
  const targetX = lanePositions[lane]

  useFrame(({ clock }) => {
    const time = clock.getElapsedTime()
    // Tốc độ animation chạy tăng theo tốc độ game (base 10, nhân với speed)
    const runSpeed = 10 * speed // Tốc độ chạy tăng theo speed game
    
    if (meshRef.current) {
      // Khởi tạo jump start time khi bắt đầu nhảy
      if (isJumping && jumpStartTimeRef.current === null) {
        jumpStartTimeRef.current = time
        jumpStartXRef.current = meshRef.current.position.x
      }
      
      // Reset jump start time khi không nhảy
      if (!isJumping && jumpStartTimeRef.current !== null) {
        jumpStartTimeRef.current = null
      }
      
      // Animation nhảy cao vượt qua với quỹ đạo parabol (ném xiên)
      let jumpHeight = 0
      let jumpForward = 0
      if (isJumping && jumpStartTimeRef.current !== null) {
        const jumpElapsed = time - jumpStartTimeRef.current
        const jumpDuration = 0.9 // Thời gian nhảy 0.9 giây
        const jumpProgress = Math.min(1, jumpElapsed / jumpDuration)
        
        if (jumpProgress < 1) {
          // Quỹ đạo parabol: nhảy lên cao và về phía trước
          const maxHeight = 2.8 // Độ cao tối đa
          const maxForward = 1.5 // Khoảng cách nhảy về phía trước
          
          // Quỹ đạo parabol: y = -a*x^2 + b*x (với x là progress, y là height)
          // Để có đỉnh ở giữa (x=0.5, y=maxHeight)
          const a = 4 * maxHeight // Hệ số parabol
          jumpHeight = -a * jumpProgress * jumpProgress + a * jumpProgress
          
          // Di chuyển về phía trước (Z giảm = về phía trước trong 3D)
          jumpForward = -jumpProgress * maxForward
          
          // Nghiêng người về phía trước khi nhảy
          meshRef.current.rotation.x = jumpProgress * 0.3 // Nghiêng về phía trước
        } else {
          // Kết thúc nhảy, reset về vị trí bình thường
          jumpStartTimeRef.current = null
          meshRef.current.rotation.x = 0
        }
      } else {
        // Không nhảy, reset rotation
        meshRef.current.rotation.x = 0
      }
      
      // Di chuyển mượt mà đến làn đúng (chỉ khi không nhảy)
      if (!isJumping || jumpStartTimeRef.current === null) {
        meshRef.current.position.x = THREE.MathUtils.lerp(meshRef.current.position.x, targetX, 0.2)
      } else {
        // Khi nhảy, giữ nguyên vị trí X ban đầu
        meshRef.current.position.x = jumpStartXRef.current
      }
      
      // Animation chạy chuyên nghiệp: nhún nhẹ theo nhịp chạy (chỉ khi không nhảy)
      const bounce = isJumping ? 0 : Math.abs(Math.sin(time * runSpeed)) * 0.08
      meshRef.current.position.y = 0.5 + bounce + jumpHeight
      meshRef.current.position.z = PLAYER_Z_POSITION + jumpForward
      
      // Nghiêng nhẹ thân người khi chuyển làn (lean into turn) - chỉ khi không nhảy
      if (!isJumping) {
        const leanAmount = (targetX - meshRef.current.position.x) * 0.3
        meshRef.current.rotation.z = THREE.MathUtils.lerp(meshRef.current.rotation.z, leanAmount, 0.1)
      }
    }

    if (headRef.current) {
      // Đầu xoay nhẹ trái phải khi chạy (không di chuyển position để tránh mất đầu)
      headRef.current.rotation.y = Math.sin(time * 1.5) * 0.1
    }

    // Animation chân chạy chuyên nghiệp - chân vung cao và nhanh
    if (leftLegRef.current && rightLegRef.current) {
      const legSwing = Math.sin(time * runSpeed) * 0.8 // Vung chân cao hơn
      const legLift = Math.max(0, Math.sin(time * runSpeed)) * 0.3 // Nhấc chân lên
      
      leftLegRef.current.rotation.x = legSwing
      rightLegRef.current.rotation.x = -legSwing
      // Nhấc chân lên khi vung về phía sau
      leftLegRef.current.position.y = legLift
      rightLegRef.current.position.y = Math.max(0, -Math.sin(time * runSpeed)) * 0.3
    }

    // Animation tay chạy chuyên nghiệp - vung tay mạnh và đồng bộ với chân
    if (leftArmRef.current && rightArmRef.current) {
      const armSwing = Math.sin(time * runSpeed) * 0.7 // Vung tay mạnh hơn
      const armBend = Math.abs(Math.sin(time * runSpeed)) * 0.4 // Gập khuỷu tay
      
      // Tay đối xứng với chân (tay trái với chân phải)
      leftArmRef.current.rotation.x = -armSwing - armBend
      rightArmRef.current.rotation.x = armSwing + armBend
      
      // Vung tay ra ngoài khi chạy
      leftArmRef.current.rotation.z = -0.2 - Math.abs(Math.sin(time * runSpeed)) * 0.1
      rightArmRef.current.rotation.z = 0.2 + Math.abs(Math.sin(time * runSpeed)) * 0.1
      
      // Tay hơi đưa về phía trước khi chạy
      leftArmRef.current.position.z = Math.sin(time * runSpeed) * 0.1
      rightArmRef.current.position.z = -Math.sin(time * runSpeed) * 0.1
    }
  })

  return (
    <group ref={meshRef} position={[0, 0.5, PLAYER_Z_POSITION]} rotation={[0, Math.PI, 0]} scale={1.3}>
      {/* === ĐẦU === - Thiết kế đơn giản nhưng đẹp */}
      <group ref={headRef} position={[0, 0.9, 0]}>
        {/* Đầu chính - lớn hơn, tròn hơn */}
        <mesh position={[0, 0, 0]}>
          <sphereGeometry args={[0.35, GEOMETRY_QUALITY.sphereSegments, GEOMETRY_QUALITY.sphereSegments]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.6} />
        </mesh>

        {/* Tóc - style đơn giản, chỉ phần trên đầu, không che mặt */}
        <mesh position={[0, 0.15, -0.05]}>
          <boxGeometry args={[0.45, 0.18, 0.35]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.8} />
        </mesh>

        {/* Mắt trái */}
        <mesh position={[-0.13, 0.1, 0.3]}>
          <boxGeometry args={[0.08, 0.06, 0.02]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
        <mesh position={[-0.13, 0.1, 0.32]}>
          <boxGeometry args={[0.04, 0.04, 0.02]} />
          <meshStandardMaterial color="#000000" />
        </mesh>

        {/* Mắt phải */}
        <mesh position={[0.13, 0.1, 0.3]}>
          <boxGeometry args={[0.08, 0.06, 0.02]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
        <mesh position={[0.13, 0.1, 0.32]}>
          <boxGeometry args={[0.04, 0.04, 0.02]} />
          <meshStandardMaterial color="#000000" />
        </mesh>

        {/* Miệng cười đơn giản */}
        <mesh position={[0, -0.05, 0.3]} rotation={[0, 0, 0]}>
          <boxGeometry args={[0.15, 0.03, 0.02]} />
          <meshStandardMaterial color="#ff6b9d" roughness={0.6} />
        </mesh>
      </group>

      {/* === CỔ === */}
      <mesh position={[0, 0.55, 0]}>
        <cylinderGeometry args={[0.12, 0.12, 0.15, GEOMETRY_QUALITY.cylinderSegments]} />
        <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
      </mesh>

      {/* === THÂN === - Style runner chuyên nghiệp */}
      {/* Áo thun thể thao */}
      <mesh position={[0, 0.15, 0]}>
        <boxGeometry args={[0.5, 0.7, 0.35]} />
        <meshStandardMaterial color="#10b981" roughness={0.7} />
      </mesh>

      {/* Logo trên áo - đơn giản hơn */}
      <mesh position={[0, 0.3, 0.3]}>
        <boxGeometry args={[0.12, 0.12, 0.05]} />
        <meshStandardMaterial color="#fbbf24" emissive="#fbbf24" emissiveIntensity={0.4} />
      </mesh>

      {/* Vạch kẻ thể thao */}
      <mesh position={[-0.2, 0.15, 0.3]}>
        <boxGeometry args={[0.05, 0.6, 0.02]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.2, 0.15, 0.3]}>
        <boxGeometry args={[0.05, 0.6, 0.02]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>

      {/* === TAY TRÁI === - Style runner */}
      <group ref={leftArmRef} position={[-0.28, 0.3, 0.2]}>
        {/* Vai */}
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[0.12, 0.12, 0.12]} />
          <meshStandardMaterial color="#10b981" roughness={0.7} />
        </mesh>
        
        {/* Cánh tay trên */}
        <mesh position={[0, -0.2, 0]}>
          <boxGeometry args={[0.1, 0.25, 0.1]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
        </mesh>

        {/* Cánh tay dưới */}
        <mesh position={[0, -0.45, 0]}>
          <boxGeometry args={[0.08, 0.25, 0.08]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
        </mesh>

        {/* Bàn tay - đơn giản */}
        <mesh position={[0, -0.6, 0]}>
          <boxGeometry args={[0.1, 0.08, 0.1]} />
          <meshStandardMaterial color="#ffc896" roughness={0.7} />
        </mesh>
      </group>

      {/* === TAY PHẢI === */}
      <group ref={rightArmRef} position={[0.28, 0.3, 0.2]}>
        {/* Vai */}
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[0.12, 0.12, 0.12]} />
          <meshStandardMaterial color="#10b981" roughness={0.7} />
        </mesh>
        
        {/* Cánh tay trên */}
        <mesh position={[0, -0.2, 0]}>
          <boxGeometry args={[0.1, 0.25, 0.1]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
        </mesh>

        {/* Cánh tay dưới */}
        <mesh position={[0, -0.45, 0]}>
          <boxGeometry args={[0.08, 0.25, 0.08]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
        </mesh>

        {/* Bàn tay */}
        <mesh position={[0, -0.6, 0]}>
          <boxGeometry args={[0.1, 0.08, 0.1]} />
          <meshStandardMaterial color="#ffc896" roughness={0.7} />
        </mesh>
      </group>

      {/* === CHÂN TRÁI === - Style runner chuyên nghiệp */}
      <group ref={leftLegRef} position={[-0.12, -0.25, 0]}>
        {/* Quần thể thao */}
        <mesh position={[0, 0.05, 0]}>
          <boxGeometry args={[0.22, 0.4, 0.25]} />
          <meshStandardMaterial color="#3b82f6" roughness={0.7} />
        </mesh>
        
        {/* Đùi */}
        <mesh position={[0, -0.15, 0]}>
          <boxGeometry args={[0.18, 0.3, 0.2]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
        </mesh>

        {/* Ống chân */}
        <mesh position={[0, -0.35, 0]}>
          <boxGeometry args={[0.15, 0.25, 0.18]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
        </mesh>

        {/* Giày thể thao - lớn hơn, đẹp hơn */}
        <mesh position={[0, -0.52, 0.08]}>
          <boxGeometry args={[0.2, 0.12, 0.3]} />
          <meshStandardMaterial color="#1f2937" roughness={0.4} />
        </mesh>
        {/* Đế giày */}
        <mesh position={[0, -0.58, 0.1]}>
          <boxGeometry args={[0.22, 0.04, 0.32]} />
          <meshStandardMaterial color="#000000" roughness={0.3} />
        </mesh>
        {/* Logo giày */}
        <mesh position={[0.1, -0.52, 0.15]}>
          <boxGeometry args={[0.03, 0.08, 0.05]} />
          <meshStandardMaterial color="#fbbf24" emissive="#fbbf24" emissiveIntensity={0.5} />
        </mesh>
      </group>

      {/* === CHÂN PHẢI === */}
      <group ref={rightLegRef} position={[0.12, -0.25, 0]}>
        {/* Quần thể thao */}
        <mesh position={[0, 0.05, 0]}>
          <boxGeometry args={[0.22, 0.4, 0.25]} />
          <meshStandardMaterial color="#3b82f6" roughness={0.7} />
        </mesh>
        
        {/* Đùi */}
        <mesh position={[0, -0.15, 0]}>
          <boxGeometry args={[0.18, 0.3, 0.2]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
        </mesh>

        {/* Ống chân */}
        <mesh position={[0, -0.35, 0]}>
          <boxGeometry args={[0.15, 0.25, 0.18]} />
          <meshStandardMaterial color="#ffd4a3" roughness={0.7} />
        </mesh>

        {/* Giày thể thao */}
        <mesh position={[0, -0.52, 0.08]}>
          <boxGeometry args={[0.2, 0.12, 0.3]} />
          <meshStandardMaterial color="#1f2937" roughness={0.4} />
        </mesh>
        {/* Đế giày */}
        <mesh position={[0, -0.58, 0.1]}>
          <boxGeometry args={[0.22, 0.04, 0.32]} />
          <meshStandardMaterial color="#000000" roughness={0.3} />
        </mesh>
        {/* Logo giày */}
        <mesh position={[0.1, -0.52, 0.15]}>
          <boxGeometry args={[0.03, 0.08, 0.05]} />
          <meshStandardMaterial color="#fbbf24" emissive="#fbbf24" emissiveIntensity={0.5} />
        </mesh>
      </group>

      {/* === BACKPACK === - Style runner hiện đại */}
      <mesh position={[0, 0.2, -0.25]}>
        <boxGeometry args={[0.35, 0.45, 0.18]} />
        <meshStandardMaterial color="#8b5cf6" roughness={0.7} />
      </mesh>
      {/* Vạch kẻ backpack */}
      <mesh position={[0, 0.15, -0.3]}>
        <boxGeometry args={[0.38, 0.05, 0.02]} />
        <meshStandardMaterial color="#6d28d9" />
      </mesh>

      {/* Dây backpack trái - dày hơn */}
      <mesh position={[-0.12, 0.35, -0.08]} rotation={[0.2, 0, 0]}>
        <boxGeometry args={[0.04, 0.04, 0.4]} />
        <meshStandardMaterial color="#6d28d9" roughness={0.7} />
      </mesh>

      {/* Dây backpack phải */}
      <mesh position={[0.12, 0.35, -0.08]} rotation={[0.2, 0, 0]}>
        <boxGeometry args={[0.04, 0.04, 0.4]} />
        <meshStandardMaterial color="#6d28d9" roughness={0.7} />
      </mesh>
    </group>
  )
}

/**
 * Component chướng ngại vật
 */
const ObstacleBlock: React.FC<{ obstacle: Obstacle }> = ({ obstacle }) => {
  const meshRef = useRef<THREE.Group>(null)
  const lanePositions = [-2, 0, 2]
  const x = lanePositions[obstacle.lane]

  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.position.z = obstacle.position
    }
  })

  return (
    <group ref={meshRef} position={[x, 0, obstacle.position]}>
      {/* Khối chướng ngại vật - Tất cả cùng màu */}
      <mesh>
        <boxGeometry args={[1.5, 1.5, 1.5]} />
        <meshStandardMaterial 
          color="#ef4444"
          emissive="#ef4444"
          emissiveIntensity={0.3}
        />
      </mesh>

      {/* Text đáp án - nhỏ hơn để phù hợp với ô */}
      <Html
        position={[0, 1.2, 0]}
        center
        transform
        style={{ pointerEvents: 'none' }}
      >
        <div
          style={{
            background: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 'bold',
            textAlign: 'center',
            minWidth: '80px',
            maxWidth: '120px',
            border: '2px solid #ef4444',
            boxShadow: '0 2px 4px rgba(0,0,0,0.7)',
            lineHeight: '1.2',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          {obstacle.answer}
        </div>
      </Html>
    </group>
  )
}

/**
 * Component cây - đơn giản
 */
const Tree: React.FC<{ position: [number, number, number] }> = ({ position }) => {
  return (
    <group position={position}>
      {/* Thân cây */}
      <mesh position={[0, 0.5, 0]}>
        <cylinderGeometry args={[0.1, 0.1, 1, GEOMETRY_QUALITY.cylinderSegments]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>
      {/* Tán cây */}
      <mesh position={[0, 1.2, 0]}>
        <coneGeometry args={[0.6, 0.8, GEOMETRY_QUALITY.coneSegments]} />
        <meshStandardMaterial color="#228b22" />
      </mesh>
      <mesh position={[0, 1.5, 0]}>
        <coneGeometry args={[0.5, 0.6, GEOMETRY_QUALITY.coneSegments]} />
        <meshStandardMaterial color="#32cd32" />
      </mesh>
    </group>
  )
}

/**
 * Component nhà - đơn giản
 */
const House: React.FC<{ position: [number, number, number] }> = ({ position }) => {
  return (
    <group position={position}>
      {/* Thân nhà */}
      <mesh position={[0, 0.5, 0]}>
        <boxGeometry args={[0.8, 1, 0.8]} />
        <meshStandardMaterial color="#d2691e" />
      </mesh>
      {/* Mái nhà */}
      <mesh position={[0, 1.2, 0]} rotation={[0, Math.PI / 4, 0]}>
        <coneGeometry args={[0.7, 0.6, GEOMETRY_QUALITY.coneSegments]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>
      {/* Cửa */}
      <mesh position={[0, 0.3, 0.41]}>
        <boxGeometry args={[0.2, 0.4, 0.05]} />
        <meshStandardMaterial color="#654321" />
      </mesh>
      {/* Cửa sổ trái */}
      <mesh position={[-0.3, 0.7, 0.41]}>
        <boxGeometry args={[0.15, 0.15, 0.05]} />
        <meshStandardMaterial color="#87ceeb" />
      </mesh>
      {/* Cửa sổ phải */}
      <mesh position={[0.3, 0.7, 0.41]}>
        <boxGeometry args={[0.15, 0.15, 0.05]} />
        <meshStandardMaterial color="#87ceeb" />
      </mesh>
    </group>
  )
}

/**
 * Component cảnh vật 2 bên đường - cây và nhà di chuyển cùng tốc độ, xuất hiện liên tục
 */
const SideObjects: React.FC<{ speed: number }> = ({ speed }) => {
  const leftObjectsRef = useRef<THREE.Group[]>([])
  const rightObjectsRef = useRef<THREE.Group[]>([])
  
  const numObjectsPerSide = 12 // Số cây/nhà mỗi bên
  const minSpacing = 12 // Khoảng cách tối thiểu
  const maxSpacing = 25 // Khoảng cách tối đa
  
  // Tạo khoảng cách ngẫu nhiên cho mỗi vật thể - chỉ tạo một lần
  const { spacingArray, totalLength } = useMemo(() => {
    const arr: number[] = []
    let length = 0
    for (let i = 0; i < numObjectsPerSide; i++) {
      const spacing = minSpacing + Math.random() * (maxSpacing - minSpacing)
      arr.push(spacing)
      length += spacing
    }
    return { spacingArray: arr, totalLength: length }
  }, []) // Chỉ tạo một lần khi component mount

  // Khởi tạo refs
  useEffect(() => {
    leftObjectsRef.current = []
    rightObjectsRef.current = []
  }, [])

  useFrame(() => {
    const moveSpeed = speed * 0.1
    
    // Di chuyển vật thể bên trái
    leftObjectsRef.current.forEach((group, index) => {
      if (group) {
        group.position.z += moveSpeed
        // Loop lại khi đi quá xa (về phía sau người chơi)
        if (group.position.z > 15) {
          // Tìm vật thể xa nhất về phía trước (có z nhỏ nhất)
          const otherGroups = leftObjectsRef.current
            .filter(g => g !== null && g !== group && g.position.z <= 15)
            .map(g => g!.position.z)
          
          if (otherGroups.length > 0) {
            const farthestZ = Math.min(...otherGroups) // Z nhỏ nhất = xa nhất về phía trước
            // Sử dụng khoảng cách tương ứng với index
            const spacing = spacingArray[index] || minSpacing
            group.position.z = farthestZ - spacing // Đặt phía sau vật thể xa nhất
          } else {
            // Fallback: reset về vị trí ban đầu
            let resetZ = -totalLength / 2
            for (let i = 0; i < index; i++) {
              resetZ += spacingArray[i] || minSpacing
            }
            group.position.z = resetZ
          }
        }
      }
    })

    // Di chuyển vật thể bên phải
    rightObjectsRef.current.forEach((group, index) => {
      if (group) {
        group.position.z += moveSpeed
        // Loop lại khi đi quá xa (về phía sau người chơi)
        if (group.position.z > 15) {
          // Tìm vật thể xa nhất về phía trước (có z nhỏ nhất)
          const otherGroups = rightObjectsRef.current
            .filter(g => g !== null && g !== group && g.position.z <= 15)
            .map(g => g!.position.z)
          
          if (otherGroups.length > 0) {
            const farthestZ = Math.min(...otherGroups) // Z nhỏ nhất = xa nhất về phía trước
            // Sử dụng khoảng cách tương ứng với index
            const spacing = spacingArray[index] || minSpacing
            group.position.z = farthestZ - spacing // Đặt phía sau vật thể xa nhất
          } else {
            // Fallback: reset về vị trí ban đầu
            let resetZ = -totalLength / 2
            for (let i = 0; i < index; i++) {
              resetZ += spacingArray[i] || minSpacing
            }
            group.position.z = resetZ
          }
        }
      }
    })
  })

  // Tạo danh sách vật thể với khoảng cách ngẫu nhiên
  const leftSideObjects: Array<{ type: 'tree' | 'house'; z: number }> = []
  const rightSideObjects: Array<{ type: 'tree' | 'house'; z: number }> = []
  
  let currentZ = -totalLength / 2
  for (let i = 0; i < numObjectsPerSide; i++) {
    // Xen kẽ cây và nhà
    leftSideObjects.push({
      type: i % 3 === 0 ? 'house' : 'tree',
      z: currentZ
    })
    rightSideObjects.push({
      type: i % 3 === 1 ? 'house' : 'tree',
      z: currentZ
    })
    
    // Tăng z theo khoảng cách ngẫu nhiên
    currentZ += spacingArray[i] || minSpacing
  }

  return (
    <>
      {/* Bên trái đường */}
      {leftSideObjects.map((obj, index) => (
        <group
          key={`left-${index}`}
          ref={(el) => {
            if (el) leftObjectsRef.current[index] = el
          }}
          position={[0, 0, obj.z]}
        >
          {obj.type === 'tree' ? (
            <Tree position={[-4.5, 0, 0]} />
          ) : (
            <House position={[-4.5, 0, 0]} />
          )}
        </group>
      ))}
      
      {/* Bên phải đường */}
      {rightSideObjects.map((obj, index) => (
        <group
          key={`right-${index}`}
          ref={(el) => {
            if (el) rightObjectsRef.current[index] = el
          }}
          position={[0, 0, obj.z]}
        >
          {obj.type === 'tree' ? (
            <Tree position={[4.5, 0, 0]} />
          ) : (
            <House position={[4.5, 0, 0]} />
          )}
        </group>
      ))}
    </>
  )
}

/**
 * Component đường chạy với 3 làn - di chuyển liên tục không ngắt quãng
 */
const Road: React.FC<{ speed: number }> = ({ speed }) => {
  const roadSegmentsRef = useRef<THREE.Group[]>([])
  const leftLineGroupsRef = useRef<THREE.Group[]>([])
  const rightLineGroupsRef = useRef<THREE.Group[]>([])
  
  const segmentLength = 50 // Chiều dài mỗi đoạn đường
  const numSegments = 6 // Tăng số đoạn để đảm bảo không bị gián đoạn
  const lineSpacing = 2.5
  const numDashesPerSegment = Math.floor(segmentLength / (lineSpacing * 2)) // Số nét đứt mỗi đoạn

  // Khởi tạo refs cho các đoạn đường
  useEffect(() => {
    roadSegmentsRef.current = []
    leftLineGroupsRef.current = []
    rightLineGroupsRef.current = []
  }, [])

  useFrame(() => {
    const moveSpeed = speed * 0.1
    
    // Di chuyển tất cả các đoạn đường
    roadSegmentsRef.current.forEach((segment, index) => {
      if (segment) {
        segment.position.z += moveSpeed
        
        // Loop lại khi đoạn đường đi quá xa (về phía sau người chơi)
        // Tìm đoạn xa nhất về phía trước (z nhỏ nhất) và đặt đoạn này ngay sau nó
        if (segment.position.z > segmentLength) {
          const otherSegments = roadSegmentsRef.current
            .filter(s => s !== null && s !== segment && s.position.z <= segmentLength)
            .map(s => s!.position.z)
          
          if (otherSegments.length > 0) {
            // Z nhỏ nhất = xa nhất về phía trước
            const farthestZ = Math.min(...otherSegments)
            // Đặt ngay sau đoạn xa nhất, không có khoảng trống
            segment.position.z = farthestZ - segmentLength
          } else {
            // Nếu không tìm thấy, tìm đoạn xa nhất về phía trước (z nhỏ nhất)
            const allOtherZ = roadSegmentsRef.current
              .filter(s => s !== null && s !== segment)
              .map(s => s!.position.z)
            
            if (allOtherZ.length > 0) {
              const farthestZ = Math.min(...allOtherZ)
              segment.position.z = farthestZ - segmentLength
            } else {
              // Fallback: reset về vị trí ban đầu
              segment.position.z = -segmentLength * numSegments / 2 + index * segmentLength
            }
          }
        }
      }
    })

    // Di chuyển vạch kẻ trái (nét đứt) - di chuyển cùng với các đoạn đường
    leftLineGroupsRef.current.forEach((group, index) => {
      if (group) {
        group.position.z += moveSpeed
        if (group.position.z > segmentLength) {
          const otherGroups = leftLineGroupsRef.current
            .filter(g => g !== null && g !== group && g.position.z <= segmentLength)
            .map(g => g!.position.z)
          
          if (otherGroups.length > 0) {
            // Z nhỏ nhất = xa nhất về phía trước
            const farthestZ = Math.min(...otherGroups)
            group.position.z = farthestZ - segmentLength
          } else {
            const allOtherZ = leftLineGroupsRef.current
              .filter(g => g !== null && g !== group)
              .map(g => g!.position.z)
            
            if (allOtherZ.length > 0) {
              const farthestZ = Math.min(...allOtherZ)
              group.position.z = farthestZ - segmentLength
            } else {
              group.position.z = -segmentLength * numSegments / 2 + index * segmentLength
            }
          }
        }
      }
    })

    // Di chuyển vạch kẻ phải (nét đứt) - di chuyển cùng với các đoạn đường
    rightLineGroupsRef.current.forEach((group, index) => {
      if (group) {
        group.position.z += moveSpeed
        if (group.position.z > segmentLength) {
          const otherGroups = rightLineGroupsRef.current
            .filter(g => g !== null && g !== group && g.position.z <= segmentLength)
            .map(g => g!.position.z)
          
          if (otherGroups.length > 0) {
            // Z nhỏ nhất = xa nhất về phía trước
            const farthestZ = Math.min(...otherGroups)
            group.position.z = farthestZ - segmentLength
          } else {
            const allOtherZ = rightLineGroupsRef.current
              .filter(g => g !== null && g !== group)
              .map(g => g!.position.z)
            
            if (allOtherZ.length > 0) {
              const farthestZ = Math.min(...allOtherZ)
              group.position.z = farthestZ - segmentLength
            } else {
              group.position.z = -segmentLength * numSegments / 2 + index * segmentLength
            }
          }
        }
      }
    })

    // Bỏ vạch kẻ giữa
  })

  return (
    <>
      {/* Mặt đường - nhiều đoạn liên tục, nối liền mạch */}
      {Array.from({ length: numSegments }).map((_, i) => {
        const baseZ = -segmentLength * numSegments / 2
        const segmentZ = baseZ + i * segmentLength
        return (
          <group 
            key={`road-${i}`} 
            ref={(el) => {
              if (el) roadSegmentsRef.current[i] = el
            }}
            position={[0, 0, segmentZ]}
          >
            <mesh 
              rotation={[-Math.PI / 2, 0, 0]} 
              position={[0, -0.5, 0]} 
              receiveShadow
            >
              <planeGeometry args={[8, segmentLength]} />
              <meshStandardMaterial color="#4a5568" />
            </mesh>
          </group>
        )
      })}

      {/* Vạch kẻ đường trái - nét đứt */}
      {Array.from({ length: numSegments }).map((_, segmentIndex) => {
        const baseZ = -segmentLength * numSegments / 2
        const segmentZ = baseZ + segmentIndex * segmentLength
        
        return (
          <group 
            key={`left-line-group-${segmentIndex}`}
            ref={(el) => {
              if (el) leftLineGroupsRef.current[segmentIndex] = el
            }}
            position={[0, 0, segmentZ]}
          >
            {Array.from({ length: numDashesPerSegment }).map((_, dashIndex) => {
              const dashZ = -segmentLength / 2 + dashIndex * lineSpacing * 2
              return (
                <mesh 
                  key={`left-line-${segmentIndex}-${dashIndex}`}
                  rotation={[-Math.PI / 2, 0, 0]} 
                  position={[-1, -0.49, dashZ]}
                >
                  <planeGeometry args={[0.1, lineSpacing]} />
                  <meshStandardMaterial color="#fbbf24" emissive="#fbbf24" emissiveIntensity={0.8} />
                </mesh>
              )
            })}
          </group>
        )
      })}

      {/* Vạch kẻ đường phải - nét đứt */}
      {Array.from({ length: numSegments }).map((_, segmentIndex) => {
        const baseZ = -segmentLength * numSegments / 2
        const segmentZ = baseZ + segmentIndex * segmentLength
        
        return (
          <group 
            key={`right-line-group-${segmentIndex}`}
            ref={(el) => {
              if (el) rightLineGroupsRef.current[segmentIndex] = el
            }}
            position={[0, 0, segmentZ]}
          >
            {Array.from({ length: numDashesPerSegment }).map((_, dashIndex) => {
              const dashZ = -segmentLength / 2 + dashIndex * lineSpacing * 2
              return (
                <mesh 
                  key={`right-line-${segmentIndex}-${dashIndex}`}
                  rotation={[-Math.PI / 2, 0, 0]} 
                  position={[1, -0.49, dashZ]}
                >
                  <planeGeometry args={[0.1, lineSpacing]} />
                  <meshStandardMaterial color="#fbbf24" emissive="#fbbf24" emissiveIntensity={0.8} />
                </mesh>
              )
            })}
          </group>
        )
      })}

      {/* Bỏ vạch tim đường */}
    </>
  )
}

/**
 * Scene 3D chính
 */
const SurvivalRunnerScene3D: React.FC<SurvivalRunnerSceneProps> = ({
  currentLane,
  obstacles,
  currentQuestion,
  speed,
  isJumping
}) => {
  const cameraRef = useRef<THREE.PerspectiveCamera>(null)

  useFrame(() => {
    if (cameraRef.current) {
      // Camera follow nhân vật - góc nhìn từ phía sau và trên, hạ thấp một chút
      cameraRef.current.position.x = 0
      cameraRef.current.position.y = 4.5 // Hạ thấp từ 5.5 xuống 4.5
      cameraRef.current.position.z = PLAYER_Z_POSITION + 6.5 // Giữ nguyên khoảng cách
      cameraRef.current.lookAt(0, 1.0, PLAYER_Z_POSITION - 2) // Hạ điểm nhìn xuống một chút
    }
  })

  return (
    <>
      {/* Ánh sáng - tối ưu cho mobile */}
      <ambientLight intensity={0.8} />
      {isMobile ? (
        // Trên mobile: chỉ dùng ambient và 1 directional light
        <directionalLight position={[5, 10, 5]} intensity={1.2} />
      ) : (
        // Trên desktop: đầy đủ lighting với shadows
        <>
          <directionalLight position={[5, 10, 5]} intensity={1.5} castShadow />
          <pointLight position={[0, 5, 3]} intensity={1} />
          <pointLight position={[0, 2, 0]} intensity={0.8} color="#ffffff" />
        </>
      )}

      {/* Đường chạy - di chuyển cùng tốc độ game */}
      <Road speed={speed} />

      {/* Cảnh vật 2 bên đường - cây và nhà */}
      <SideObjects speed={speed} />

      {/* Nhân vật - tốc độ chạy tăng theo speed game */}
      <RunningCharacter lane={currentLane} speed={speed} isJumping={isJumping} />

      {/* Chướng ngại vật */}
      {obstacles.map(obstacle => (
        <ObstacleBlock key={obstacle.id} obstacle={obstacle} />
      ))}

      {/* Banner câu hỏi - di chuyển theo chướng ngại vật gần nhất, chỉ hiển thị khi có obstacle */}
      {(() => {
        // Tìm chướng ngại vật gần nhất (có position lớn nhất, tức là gần người chơi nhất)
        // Tìm trong tất cả obstacles, không lọc
        if (obstacles.length === 0) return null
        
        const nearestObstacle = obstacles.reduce((nearest, obs) => 
          obs.position > nearest.position ? obs : nearest
        )
        
        // Vị trí Z của banner = vị trí của chướng ngại vật gần nhất + offset phía trên
        const bannerZ = nearestObstacle.position + 0.3
        
        return (
          <group position={[0, 2.2, bannerZ]}>
            {/* Chỉ hiển thị text, không có nền - Đặt gần sát với các phương án */}
            <Html
              position={[0, 0, 0]}
              center
              transform
              style={{ pointerEvents: 'none' }}
            >
              <div
                style={{
                  width: '600px',
                  maxWidth: '90vw',
                  textAlign: 'center',
                  color: 'white',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  padding: '12px 16px',
                  textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.7)',
                  lineHeight: '1.4',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word'
                }}
              >
                {currentQuestion || 'Chuẩn bị...'}
              </div>
            </Html>
          </group>
        )
      })()}
    </>
  )
}

/**
 * Component wrapper với Canvas
 */
export const SurvivalRunnerScene: React.FC<SurvivalRunnerSceneProps> = (props) => {
  // Tối ưu Canvas settings cho mobile
  const canvasSettings = useMemo(() => ({
    dpr: isMobile ? [1, 1.5] as [number, number] : [1, 2] as [number, number], // Giảm DPR trên mobile
    shadows: !isMobile, // Tắt shadows trên mobile
    antialias: !isMobile, // Tắt antialiasing trên mobile để tăng performance
    gl: {
      antialias: !isMobile,
      powerPreference: 'high-performance' as WebGLPowerPreference,
      alpha: false,
      stencil: false,
      depth: true
    }
  }), [])

  return (
    <div className="w-full h-full">
      <Canvas
        camera={{ position: [0, 4.5, PLAYER_Z_POSITION + 6.5], fov: 70 }}
        shadows={canvasSettings.shadows}
        dpr={canvasSettings.dpr}
        gl={canvasSettings.gl}
        style={{ background: 'linear-gradient(to bottom, #87CEEB, #E0F6FF)' }}
      >
        <SurvivalRunnerScene3D {...props} />
      </Canvas>
    </div>
  )
}

